import { Bot, Copy, Languages, Loader2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraftCopy, type IntegrationLocale } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { LOCALES, copyFor, errorMessage, type StepProps } from '../builderModel';
import { Field, IntegrationCardPreview, StepFrame } from '../builderUi';

type CopyState = Record<IntegrationLocale, IntegrationDraftCopy>;

function Counter({ value, max }: { value: string; max: number }) {
  return (
    <span className={cn('font-mono text-[11px]', value.length > max ? 'text-destructive' : 'text-muted-foreground')}>
      {value.length}/{max}
    </span>
  );
}

function localeComplete(copy: IntegrationDraftCopy): boolean {
  return Boolean(copy.title && copy.description && copy.longDescription && copy.agentGuidance);
}

export function CopyStep({ draft, readOnly, saving, save, replace, next }: StepProps) {
  const initial = (): CopyState =>
    Object.fromEntries(LOCALES.map((locale) => [locale.id, copyFor(draft, locale.id)])) as CopyState;
  const [copy, setCopy] = useState<CopyState>(initial);
  const [locale, setLocale] = useState<IntegrationLocale>('en');
  const [newCapability, setNewCapability] = useState('');
  const [translating, setTranslating] = useState<IntegrationLocale | 'all'>();

  const current = copy[locale];
  const dirty = LOCALES.some((item) => JSON.stringify(copy[item.id]) !== JSON.stringify(copyFor(draft, item.id)));

  const update = (patch: Partial<IntegrationDraftCopy>) =>
    setCopy((state) => ({ ...state, [locale]: { ...state[locale], ...patch } }));

  const persist = () =>
    save({
      copy: Object.fromEntries(
        LOCALES.filter((item) => item.id === 'en' || copy[item.id].title.trim()).map((item) => [item.id, copy[item.id]]),
      ),
    });

  const translate = async (only?: IntegrationLocale) => {
    setTranslating(only ?? 'all');
    try {
      const result = await adminClient.translateIntegrationDraft(draft.draftId, only ? [only] : undefined);
      replace(result);
      setCopy((state) => {
        const next = { ...state };
        for (const locale of result.translation.translated) next[locale] = copyFor(result, locale);
        return next;
      });
      if (result.translation.failed.length) {
        toast.error(`Could not translate ${result.translation.failed.join(', ')}. Try again or write it by hand.`);
      } else if (!only) {
        setLocale(result.translation.translated[0] ?? locale);
      }
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setTranslating(undefined);
    }
  };

  const meta = locale === 'en' ? undefined : draft.copyMeta?.[locale];
  const stale = locale !== 'en' && draft.staleTranslations.includes(locale);

  const addCapability = () => {
    const value = newCapability.trim();
    if (!value || current.capabilities.includes(value)) return;
    update({ capabilities: [...current.capabilities, value] });
    setNewCapability('');
  };

  return (
    <StepFrame
      title="Copy & languages"
      description="Catalog copy for people, and guidance for the coding agent. Write English, then translate the other languages with one click and read them through; a language you leave empty falls back to English. Agent guidance stays English everywhere — it is a prompt, not UI copy."
      dirty={dirty}
      saving={saving}
      readOnly={readOnly}
      onSave={() => void persist()}
      onContinue={async () => {
        if (!dirty || (await persist())) next();
      }}
      aside={
        <div className="sticky top-6">
          <IntegrationCardPreview
            copy={current.title ? current : copy.en}
            category={draft.category}
            brandColor={draft.brandColor}
            logoDataUrl={draft.logoDataUrl}
            pricing={draft.pricing}
          />
        </div>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border p-1" role="tablist" aria-label="Language">
          {LOCALES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={locale === item.id}
              onClick={() => setLocale(item.id)}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                locale === item.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  localeComplete(copy[item.id]) ? 'bg-success' : copy[item.id].title ? 'bg-warning' : 'bg-muted-foreground/40',
                )}
              />
              {item.native}
              {item.id === 'en' ? <span className="text-[10px] text-muted-foreground">required</span> : null}
              {item.id !== 'en' && draft.copyMeta?.[item.id]?.source === 'machine' ? (
                <span className={cn('rounded px-1 text-[9px] uppercase', draft.staleTranslations.includes(item.id) ? 'bg-warning-bg text-warning-foreground' : 'bg-muted text-muted-foreground')}>
                  AI
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {readOnly ? null : (
          <div className="flex flex-wrap gap-2">
            {locale !== 'en' ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => update({ ...copy.en })} disabled={!copy.en.title}>
                  <Copy className="mr-2 h-3.5 w-3.5" />
                  Copy English
                </Button>
                <Button variant="outline" size="sm" onClick={() => void translate(locale)} disabled={dirty || Boolean(translating) || !copy.en.title}>
                  {translating === locale ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-2 h-3.5 w-3.5" />}
                  {copy[locale].title ? 'Retranslate' : 'Translate'}
                </Button>
              </>
            ) : null}
            <Button size="sm" onClick={() => void translate()} disabled={dirty || Boolean(translating) || !copy.en.title}>
              {translating === 'all' ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-2 h-3.5 w-3.5" />}
              Translate from English
            </Button>
          </div>
        )}
      </div>
      {!readOnly && dirty ? (
        <p className="-mt-3 text-[11px] text-muted-foreground">Save your changes before translating, so the translation starts from the latest English.</p>
      ) : null}
      {meta?.source === 'machine' ? (
        <div
          className={cn(
            'rounded-lg border px-3 py-2 text-xs',
            stale ? 'border-warning/30 bg-warning-bg/40 text-warning-foreground' : 'border-border bg-card/40 text-muted-foreground',
          )}
        >
          {stale
            ? 'English changed after this was translated. Retranslate, or edit it — review blocks until it is current.'
            : 'Machine-translated with Gemini from the English. Read it through before submitting; any edit makes it yours.'}
        </div>
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="copy-title" label="Title">
          <Input id="copy-title" value={current.title} disabled={readOnly} onChange={(event) => update({ title: event.target.value })} maxLength={80} />
        </Field>
        <Field id="copy-headline" label="Headline" optional>
          <Input
            id="copy-headline"
            value={current.headline}
            disabled={readOnly}
            onChange={(event) => update({ headline: event.target.value })}
            maxLength={160}
            placeholder="Look up any responder in seconds"
          />
        </Field>
      </div>

      <Field id="copy-description" label="Short description" hint={<Counter value={current.description} max={300} />}>
        <Textarea
          id="copy-description"
          value={current.description}
          disabled={readOnly}
          onChange={(event) => update({ description: event.target.value })}
          className="min-h-16"
        />
      </Field>

      <Field id="copy-long" label="Long description" hint="Shown on the integration's detail page. Plain text; blank lines separate paragraphs.">
        <Textarea
          id="copy-long"
          value={current.longDescription}
          disabled={readOnly}
          onChange={(event) => update({ longDescription: event.target.value })}
          className="min-h-32"
        />
      </Field>

      <Field label="What it can do" hint="Short bullet points for the catalog card. Up to 12.">
        <div className="space-y-2">
          {current.capabilities.map((capability, index) => (
            <div key={`${capability}-${index}`} className="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-sm">
              <span className="h-1 w-1 shrink-0 rounded-full bg-brand" />
              <span className="min-w-0 flex-1">{capability}</span>
              {readOnly ? null : (
                <button
                  type="button"
                  aria-label={`Remove ${capability}`}
                  onClick={() => update({ capabilities: current.capabilities.filter((_, i) => i !== index) })}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {readOnly || current.capabilities.length >= 12 ? null : (
            <div className="flex gap-2">
              <Input
                value={newCapability}
                onChange={(event) => setNewCapability(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCapability();
                  }
                }}
                placeholder="Search people by name, unit or role"
              />
              <Button variant="outline" onClick={addCapability} disabled={!newCapability.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </Field>

      {locale === 'en' ? (
        <div className="rounded-xl border border-brand/30 bg-brand-muted/30 p-5">
          <div className="mb-3 flex items-center gap-2">
            <Bot className="h-4 w-4 text-brand" />
            <span className="text-sm font-semibold">Agent guidance</span>
          </div>
          <p className="mb-3 text-xs leading-5 text-muted-foreground">
            When a builder pins this integration, these instructions go into the coding agent's prompt. Name the capability, the
            operations to call and where to call them from (the App Service, never the browser for server credentials), and what to
            render when the integration is not connected.
          </p>
          <Textarea
            aria-label="Agent guidance"
            value={current.agentGuidance}
            disabled={readOnly}
            onChange={(event) => update({ agentGuidance: event.target.value })}
            className="min-h-28 bg-background font-mono text-xs"
            placeholder={`In the App Service, call ctx.integrations.require('${draft.capability || 'directory.lookup'}') and use its listPeople operation. Never call the API from the browser. If require throws MissingCapabilityError, return its setupPath and render a connect state.`}
          />
          <div className="mt-2 flex justify-end">
            <Counter value={current.agentGuidance} max={2000} />
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-border bg-card/40 px-3 py-2 text-xs text-muted-foreground">
          Agent guidance is written once, in English, and used for every language.
        </p>
      )}

      {draft.pricing.length ? (
        <Field label="Pricing labels" hint="How each rate is named on the card in this language. The prices themselves are set on the Pricing step.">
          <div className="space-y-2">
            {draft.pricing.map((rate) => (
              <div key={rate.id} className="grid items-center gap-2 sm:grid-cols-[160px_minmax(0,1fr)]">
                <code className="truncate font-mono text-xs text-muted-foreground">{rate.id}</code>
                <Input
                  value={current.rateLabels[rate.id] ?? ''}
                  disabled={readOnly}
                  onChange={(event) => update({ rateLabels: { ...current.rateLabels, [rate.id]: event.target.value } })}
                  placeholder={copy.en.rateLabels[rate.id] || 'Label'}
                />
              </div>
            ))}
          </div>
        </Field>
      ) : null}
    </StepFrame>
  );
}
